"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { planActivation, isSleepCommand } from "@/lib/aqua/activation";
import type { ActivationEvent, ActivationPlan } from "@/lib/aqua/activation";
import { aquaToolsForContext } from "@/lib/aqua/manifest";
import { executeAquaTool } from "@/lib/aqua/operator";
import { buildAquaDrawInstructions, customerFirstName } from "@/lib/aqua/personality";
import { recordDiagnostic } from "@/lib/aqua/diagnostics";
import { ownerAccessToken, readPortalAccess } from "@/lib/cloud";
import type { AquaDiagnostic, AquaOperator, AquaStatus, AquaTurn } from "@/lib/aqua/types";

const FIRST_USE_KEY = "aquadraw:aqua:0.17:first-use-complete";
const DAILY_WAKE_KEY = "aquadraw:aqua:0.17:last-wake-date";
const IDLE_MS = 2 * 60 * 1000;
const MAX_SESSION_MS = 10 * 60 * 1000;

function turn(role: AquaTurn["role"], text: string): AquaTurn {
  return { id: crypto.randomUUID(), role, text, timestamp: new Date().toISOString() };
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Aqua could not establish the voice connection.";
}

export function useAquaVoice(operator: AquaOperator, preferredName = "Dave") {
  const [status, setStatus] = useState<AquaStatus>("standby");
  const [turns, setTurns] = useState<AquaTurn[]>([]);
  const [diagnostics, setDiagnostics] = useState<AquaDiagnostic[]>([]);
  const [error, setError] = useState("");
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const greetingTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const sessionTimerRef = useRef<number | null>(null);
  const assistantBufferRef = useRef("");
  const operatorRef = useRef(operator);
  const connectingRef = useRef<Promise<void> | null>(null);
  const sessionGenerationRef = useRef(0);

  useEffect(() => { operatorRef.current = operator; }, [operator]);

  const log = useCallback((event: string, detail: string, diagnosticStatus: AquaDiagnostic["status"] = "INFO") => {
    setDiagnostics((current) => [...current.slice(-99), { timestamp: new Date().toISOString(), event, detail, status: diagnosticStatus }]);
    recordDiagnostic({
      level: diagnosticStatus,
      category: "aqua",
      event,
      message: detail,
      view: operatorRef.current.getContext().view,
    });
  }, []);

  const clearTimers = useCallback(() => {
    if (greetingTimerRef.current) window.clearTimeout(greetingTimerRef.current);
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    if (sessionTimerRef.current) window.clearTimeout(sessionTimerRef.current);
    greetingTimerRef.current = null;
    idleTimerRef.current = null;
    sessionTimerRef.current = null;
  }, []);

  const close = useCallback((reason = "Session returned to standby.") => {
    clearTimers();
    sessionGenerationRef.current += 1;
    const channel = channelRef.current;
    channelRef.current = null;
    if (channel?.readyState === "open") {
      try { channel.send(JSON.stringify({ type: "response.cancel" })); } catch { /* already closed */ }
      try { channel.send(JSON.stringify({ type: "output_audio_buffer.clear" })); } catch { /* already closed */ }
    }
    channel?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    peerRef.current?.close();
    peerRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    connectingRef.current = null;
    assistantBufferRef.current = "";
    setStatus("standby");
    log("session.closed", reason, "PASS");
  }, [clearTimers, log]);

  const send = useCallback((payload: Record<string, unknown>) => {
    const channel = channelRef.current;
    if (!channel || channel.readyState !== "open") throw new Error("Aqua voice channel is not ready.");
    channel.send(JSON.stringify(payload));
  }, []);

  const markActivity = useCallback(() => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => close("Two-minute idle limit reached."), IDLE_MS);
  }, [close]);

  const appendAssistantDelta = useCallback((delta: string) => {
    assistantBufferRef.current += delta;
    setStatus("speaking");
  }, []);

  const finishAssistantTurn = useCallback((transcript?: string) => {
    const finalText = (transcript || assistantBufferRef.current).trim();
    assistantBufferRef.current = "";
    if (finalText) setTurns((current) => [...current, turn("assistant", finalText)]);
    setStatus("listening");
    markActivity();
  }, [markActivity]);

  const handleTool = useCallback(async (name: string, callId: string, rawArguments: string, generation: number) => {
    let args: Record<string, unknown> = {};
    try { args = rawArguments ? JSON.parse(rawArguments) as Record<string, unknown> : {}; } catch { /* fail closed below */ }
    const result = await executeAquaTool(operatorRef.current, name, args);
    if (generation !== sessionGenerationRef.current) return;
    const ok = "ok" in result ? result.ok : true;
    const verification = "verified" in result ? `; verified=${String(result.verified)}` : "";
    log("tool.executed", `${name}: ${ok ? "accepted" : "failed"}${verification}`, ok ? "PASS" : "FAIL");
    send({ type: "conversation.item.create", item: { type: "function_call_output", call_id: callId, output: JSON.stringify(result) } });
    send({ type: "response.create", response: { output_modalities: ["audio"] } });
    setStatus("thinking");
  }, [log, send]);

  const handleEvent = useCallback((raw: string) => {
    let event: Record<string, unknown>;
    try { event = JSON.parse(raw) as Record<string, unknown>; } catch { return; }
    const type = String(event.type || "");
    markActivity();
    if (type === "input_audio_buffer.speech_started") {
      if (greetingTimerRef.current) window.clearTimeout(greetingTimerRef.current);
      greetingTimerRef.current = null;
      sessionGenerationRef.current += 1;
      assistantBufferRef.current = "";
      setStatus("listening");
      log("voice.barge_in", "Pending greeting or response yielded to user speech.", "PASS");
      return;
    }
    if (type === "conversation.item.input_audio_transcription.completed") {
      const transcript = String(event.transcript || "").trim();
      if (!transcript) return;
      setTurns((current) => [...current, turn("user", transcript)]);
      log("request.voice.received", `Voice request received (${transcript.length} characters). Content omitted from diagnostic storage for customer privacy.`, "INFO");
      if (isSleepCommand(transcript)) { close("User sleep command received."); return; }
      send({ type: "response.create", response: { output_modalities: ["audio"] } });
      setStatus("thinking");
      return;
    }
    if (type === "response.output_audio_transcript.delta" || type === "response.audio_transcript.delta") {
      appendAssistantDelta(String(event.delta || ""));
      return;
    }
    if (type === "response.output_audio_transcript.done" || type === "response.audio_transcript.done") {
      finishAssistantTurn(String(event.transcript || ""));
      return;
    }
    if (type === "response.function_call_arguments.done") {
      void handleTool(String(event.name || ""), String(event.call_id || ""), String(event.arguments || "{}"), sessionGenerationRef.current);
      return;
    }
    if (type === "response.created") setStatus("thinking");
    if (type === "response.done" && !assistantBufferRef.current) setStatus("listening");
    if (type === "error") {
      const detail = typeof event.error === "object" && event.error ? String((event.error as Record<string, unknown>).message || "Realtime error") : "Realtime error";
      log("realtime.error", detail, "FAIL");
      setError("Aqua recovered from a voice-turn error and is still listening.");
      setStatus("listening");
    }
  }, [appendAssistantDelta, close, finishAssistantTurn, handleTool, log, markActivity, send]);

  const connect = useCallback(async () => {
    if (channelRef.current?.readyState === "open") return;
    if (connectingRef.current) return connectingRef.current;
    const generation = sessionGenerationRef.current + 1;
    sessionGenerationRef.current = generation;
    const connecting = (async () => {
      setError("");
      setStatus("connecting");
      log("session.connecting", "Requesting an ephemeral Realtime credential.");
      const root = typeof document === "undefined" ? null : document.documentElement;
      const realtimeUrl = root?.dataset.aquaRealtimeUrl || "";
      const publishableKey = root?.dataset.supabasePublishableKey || "";
      const nativeMode = root?.dataset.aquadrawNative === "true";
      if (nativeMode && (!realtimeUrl || !publishableKey)) {
        throw new Error("This APK is missing Aqua’s secure server configuration. Install the updated AquaDraw build.");
      }
      const context = operatorRef.current.getContext();
      const portalMode = context.portalMode;
      const resolvedName = customerFirstName(preferredName);
      const portalAccess = portalMode ? readPortalAccess() : { clientId: "", portalToken: "" };
      const accessToken = portalMode ? "" : await ownerAccessToken();
      if (!accessToken && (!portalAccess.clientId || !portalAccess.portalToken)) {
        throw new Error(portalMode ? "Open Aqua from your secure client portal link." : "Sign in to AquaDraw before starting Aqua.");
      }
      const tokenResponse = await fetch(realtimeUrl || "/api/aqua/realtime", {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(portalAccess.clientId ? { "X-AquaDraw-Portal-Client": portalAccess.clientId } : {}),
          ...(portalAccess.portalToken ? { "X-AquaDraw-Portal-Token": portalAccess.portalToken } : {}),
          ...(publishableKey ? { apikey: publishableKey } : {}),
        },
        signal: AbortSignal.timeout(15000),
      });
      const contentType = tokenResponse.headers.get("content-type") || "";
      if (!contentType.toLowerCase().includes("application/json")) {
        throw new Error(`Aqua reached the wrong service endpoint (HTTP ${tokenResponse.status}). Install the updated build.`);
      }
      const tokenPayload = await tokenResponse.json() as { value?: string; error?: string };
      if (!tokenResponse.ok || !tokenPayload.value) throw new Error(tokenPayload.error || "Aqua voice is unavailable.");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false, channelCount: 1, sampleRate: 48000 },
        video: false,
      });
      if (generation !== sessionGenerationRef.current) { stream.getTracks().forEach((track) => track.stop()); return; }
      const peer = new RTCPeerConnection();
      const channel = peer.createDataChannel("oai-events");
      const audio = audioRef.current || document.createElement("audio");
      audio.autoplay = true;
      audioRef.current = audio;
      peer.ontrack = (event) => { audio.srcObject = event.streams[0]; void audio.play().catch(() => undefined); };
      stream.getTracks().forEach((track) => peer.addTrack(track, stream));
      channel.onmessage = (event) => handleEvent(String(event.data));
      peerRef.current = peer;
      channelRef.current = channel;
      streamRef.current = stream;

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const answer = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenPayload.value}`, "Content-Type": "application/sdp" },
        body: offer.sdp,
      });
      if (!answer.ok) throw new Error(`Aqua voice connection returned HTTP ${answer.status}.`);
      await peer.setRemoteDescription({ type: "answer", sdp: await answer.text() });
      await new Promise<void>((resolve, reject) => {
        if (channel.readyState === "open") { resolve(); return; }
        const timeout = window.setTimeout(() => reject(new Error("Aqua voice channel timed out.")), 10000);
        channel.onopen = () => { window.clearTimeout(timeout); resolve(); };
        channel.onerror = () => { window.clearTimeout(timeout); reject(new Error("Aqua voice channel failed.")); };
      });
      channel.onmessage = (event) => handleEvent(String(event.data));
      channel.onclose = () => { if (channelRef.current === channel) close("Realtime channel closed."); };
      channel.onerror = () => { if (channelRef.current === channel) { setError("Aqua’s voice connection ended. Tap Ask Aqua to reconnect."); close("Realtime channel error."); } };

      send({
        type: "session.update",
        session: {
          type: "realtime",
          instructions: buildAquaDrawInstructions({ portalMode, customerName: resolvedName }),
          output_modalities: ["audio"],
          audio: {
            input: {
              transcription: { model: "gpt-4o-mini-transcribe" },
              noise_reduction: { type: "near_field" },
              turn_detection: { type: "server_vad", threshold: 0.76, prefix_padding_ms: 650, silence_duration_ms: 1100, create_response: false, interrupt_response: true },
            },
            output: { voice: "marin", speed: 1 },
          },
          tools: aquaToolsForContext(portalMode),
          tool_choice: "auto",
        },
      });
      send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text: JSON.stringify(context) }] } });
      setStatus("listening");
      log("session.connected", "Realtime data channel opened; microphone listening.", "PASS");
      markActivity();
      sessionTimerRef.current = window.setTimeout(() => close("Ten-minute maximum session reached."), MAX_SESSION_MS);
    })();
    connectingRef.current = connecting;
    try { await connecting; } finally { connectingRef.current = null; }
  }, [close, handleEvent, log, markActivity, preferredName, send]);

  const scheduleActivation = useCallback((plan: ActivationPlan) => {
    if (!plan.spokenText) return;
    greetingTimerRef.current = window.setTimeout(() => {
      send({ type: "response.create", response: { output_modalities: ["audio"], instructions: `Say exactly this, with Aqua's warm natural energy: ${plan.spokenText}` } });
      setTurns((current) => [...current, turn("assistant", plan.spokenText || "")]);
      setStatus("speaking");
      greetingTimerRef.current = null;
    }, plan.delayMs);
  }, [send]);

  const activate = useCallback(async (event: ActivationEvent, spokenCommand = "") => {
    try {
      await connect();
      const today = new Date().toISOString().slice(0, 10);
      const portalMode = operatorRef.current.getContext().portalMode;
      const portalAccess = portalMode ? readPortalAccess() : { clientId: "", portalToken: "" };
      const firstUseKey = portalMode && portalAccess.clientId ? `${FIRST_USE_KEY}:client:${portalAccess.clientId}` : FIRST_USE_KEY;
      const dailyWakeKey = portalMode && portalAccess.clientId ? `${DAILY_WAKE_KEY}:client:${portalAccess.clientId}` : DAILY_WAKE_KEY;
      const firstUse = window.localStorage.getItem(firstUseKey) !== "yes";
      const firstWakeToday = window.localStorage.getItem(dailyWakeKey) !== today;
      const resolvedName = customerFirstName(preferredName);
      const plan = planActivation({ event, firstUse, firstWakeToday, name: resolvedName, hour: new Date().getHours(), audience: portalMode ? "customer" : "owner" });
      window.localStorage.setItem(dailyWakeKey, today);
      if (firstUse) {
        window.localStorage.setItem(firstUseKey, "yes");
      }
      const command = spokenCommand.trim();
      if (command) {
        setTurns((current) => [...current, turn("user", command)]);
        log("request.wake.received", `Wake command received (${command.length} characters). Content omitted from diagnostic storage for customer privacy.`, "INFO");
        send({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{ type: "input_text", text: command }] } });
        send({ type: "response.create", response: { output_modalities: ["audio"] } });
        setStatus("thinking");
        return;
      }
      scheduleActivation(plan);
    } catch (caught) {
      const message = getErrorMessage(caught);
      setError(message);
      setStatus("error");
      log("session.failed", message, "FAIL");
      close("Connection attempt failed.");
      throw caught;
    }
  }, [close, connect, log, preferredName, scheduleActivation, send]);

  const wake = useCallback(() => activate("button"), [activate]);
  const wakeFromPhrase = useCallback((command = "") => activate(command.trim() ? "wake_with_command" : "wake_only", command), [activate]);

  const askText = useCallback(async (text: string) => {
    const request = text.trim();
    if (!request) return;
    if (isSleepCommand(request)) { close("Typed sleep command received."); return; }
    await connect();
    setTurns((current) => [...current, turn("user", request)]);
    log("request.text.received", `Typed request received (${request.length} characters). Content omitted from diagnostic storage for customer privacy.`, "INFO");
    send({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{ type: "input_text", text: request }] } });
    send({ type: "response.create", response: { output_modalities: ["audio"] } });
    setStatus("thinking");
  }, [close, connect, log, send]);

  useEffect(() => {
    const background = () => { if (document.visibilityState !== "visible") close("App moved to background."); };
    const pageExit = () => close("App page exited.");
    document.addEventListener("visibilitychange", background);
    window.addEventListener("pagehide", pageExit);
    return () => { document.removeEventListener("visibilitychange", background); window.removeEventListener("pagehide", pageExit); close("Aqua host unmounted."); };
  }, [close]);

  return { status, turns, diagnostics, error, active: status !== "standby", wake, wakeFromPhrase, askText, sleep: () => close("User tapped Sleep.") };
}
